import * as React from 'react';
import {noop} from 'lodash';
import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import {
  type ExpressionEditorDispatch,
  type ExpressionEditorActionPayload,
  type ExpressionBindMetaPayload,
  fieldWrapperLabel,
  ExpressionBinding,
  useExpressionEditorImplementationContext,
  useExpressionEditorContext,
  forwardDispatch,
  getBindingLabel,
  TypeEditor,
  isDisabledByDynamoBinding,
  getBindingIdAndPath,
} from '@packages/systems/ExpressionEditor-utils';
import {
  type ListActionResult,
  useLabelItems,
  useListActions,
} from '@packages/systems/ExpressionEditor-custom-controls';
import {reportBug} from '@packages/utilities/reporter';
import {
  type Type,
  type ExpressionPath,
  type CheckerState,
  getSystem,
  getMeta,
  withSystem,
  withMeta,
  Plugins,
  Types,
  TypeImplementation,
  Expressions,
  TypeCheckerState,
  getSubstitution,
  substituteType,
  setType,
  VariableMap,
} from '@packages/systems/wfdl';
import {
  type Qualified,
  type TypeName,
  namesEqual,
} from '@packages/systems/wfdl-Qualified';
import {
  type ContextualExpression,
  ERecord,
  EEnum,
  ContextualExpressions,
  annotate,
  stripAll,
  stripAnn,
  getAnn,
  updateAnn,
  updateExpr,
} from '@packages/utilities/contextual-expression';
import {
  enumOf,
  EUnion,
  ETyped,
  type Expression,
  EHole,
  EList,
  ERecord as eERecord,
  textTypeName,
} from '@packages/systems/wfdl-ast';
import {
  styledDiv,
  emotionStyled,
  tokens,
  styled,
} from '@packages/utilities/styled';
import {
  HStack,
  VStack,
  Note,
  View,
  Tooltip,
  HelpOutlineIcon,
  HelpCircleIcon,
  Text,
} from '@packages/systems/spring';

import {
  DeprecatedPopoverBridge,
  CLOSED_STATE,
  OPENED_STATE,
} from '@packages/systems/design-system/PopoverBridge';
import PropertyList, {
  PropertyListItem,
  PropertyListNone,
  PropertyListAddButton,
  PropertyListItemID,
} from '@packages/systems/ui/shared-ui/PropertyList';
import Button from '@packages/systems/design-system/Button';
import {getCurrentOrDefaultExpression} from '@packages/systems/core/component-conversions';
import {
  rightSidebarLabelWidth,
  rightSidebarWidth,
} from '@packages/systems/design-system/constants/DesignerUI';
import {
  ArgumentSettings,
  DataTypeSummary,
  BINDING_SELECT_BUTTON_HEIGHT,
} from '@packages/systems/ExpressionEditor-utils-ui';

import {__DEPRECATED__ChildrenPrimitiveType} from './__DEPRECATED__ChildrenPrimitiveType';
import {InterpolatedText} from '@packages/systems/ui/shared-ui/InterpolatedText';
import {usePlugins} from '@packages/utilities/platform';
import {SettingsPanelPropertyLabel} from '@packages/systems/ui/shared-ui/SettingsPanelPropertyLabel/SettingsPanelPropertyLabel';
import type {BindingPathMapPojo} from '@packages/systems/dynamo/binding-types';
import {parsePropertyLabel} from '@packages/systems/user-components/utils';

export type ExpressionEditorFocusCapture = {
  expressionPath: ExpressionPath;
  caretOffset: number | null;
};

type ExpressionEditorFocusContextValue = {
  onFieldFocused?: (
    getFocusCapture: () => ExpressionEditorFocusCapture | undefined
  ) => void;
  getFocusRequest?: () => ExpressionEditorFocusCapture | undefined;
  onFocusResolved?: () => void;
};

const EMPTY_EXPRESSION_EDITOR_FOCUS_CONTEXT: ExpressionEditorFocusContextValue =
  {};

export const ExpressionEditorFocusContext =
  React.createContext<ExpressionEditorFocusContextValue>(
    EMPTY_EXPRESSION_EDITOR_FOCUS_CONTEXT
  );

/*
 * Helper utilities
 */

/**
 * Determines the next label for a given dataType.
 * For SlotContentType, it returns 'Slot' for legacy 'Slot content' labels,
 * otherwise returns the system label or null.
 */
const getNextLabel = (
  dataType: Type,
  fallbackLabel?: string | null
): string | null => {
  const systemLabel = parsePropertyLabel(Types.getLabel(dataType)).propertyName;

  // Check if this is a SlotContentType
  if (Types.isSlotContentType(dataType)) {
    const label = fallbackLabel ?? systemLabel;
    return label === 'Slot content' ? 'Slot' : label;
  }

  // For non-SlotContentType, use fallback label or system label
  return fallbackLabel || systemLabel;
};

const unwrapAliasTypeWithSystemAndMeta = (
  type: Type,
  plugins: Plugins.PluginsType
) => {
  const ty = Types.getAliasType(type, plugins);
  // If there was no Alias type return the original type
  if (!ty) return type;
  const system = getSystem(type);
  const meta = getMeta(type);
  // In order to avoid rendering labels twice we need to force a hidden label before passing back to the alias
  const typeWithSystem = system
    ? withSystem({...system, labelVariant: 'hidden'}, ty)
    : ty;
  return meta ? withMeta({...meta}, typeWithSystem) : typeWithSystem;
};

const inDistinct: ExpressionPath = [{in: 'Distinct'}];

const createHoleRecordWithFieldValue = (
  fields: Array<string>,
  key: string,
  nextFieldExpr: Expression
): Expression =>
  eERecord(
    fields.reduce<Record<string, Expression>>((acc, curr) => {
      if (curr === key) {
        acc[curr] = nextFieldExpr;
      } else {
        acc[curr] = EHole();
      }
      return acc;
    }, {})
  );
const holeRecordFieldDispatch = (
  action: ExpressionEditorActionPayload,
  fields: Array<string>,
  key: string,
  dispatch: ExpressionEditorDispatch
) => {
  if (action.type === 'EXPRESSION_ACTION') {
    // @ilari what should happen here if action.key.length > 0?
    dispatch({
      ...action,
      payload: {
        ...action.payload,
        newExpression: createHoleRecordWithFieldValue(
          fields,
          key,
          action.payload.newExpression
        ),
      },
      key: emptyArray,
    });
  } else if (action.type === 'EXPRESSION_BIND_CHANGED') {
    // The action.binding of `EXPRESSION_BIND_CHANGED` can also be `null`, which
    // indicates an unbinding action. If our Record is a Hole at the top level
    // (we are in this function so this is true) we can never unbind a field
    // within it. So we can safely omit that case. Revert back to the default dispatch.
    if (action.binding === null) {
      forwardDispatch(dispatch)({in: 'Record', at: key})(action);
    } else {
      dispatch({
        ...action,
        binding: createHoleRecordWithFieldValue(fields, key, action.binding),
        key: emptyArray,
      });
    }
  } else if (action.type === 'EXPRESSION_LIST_ITEM_ADDED') {
    // Adding the first item to a list field inside a holey record: materialize
    // the record with holes for siblings and a single-item list at the touched
    // field. Mirrors the EXPRESSION_ACTION branch above.
    dispatch({
      type: 'EXPRESSION_ACTION',
      key: emptyArray,
      payload: {
        newExpression: createHoleRecordWithFieldValue(
          fields,
          key,
          EList([action.payload.item])
        ),
      },
    });
  } else {
    // action type === EXPRESSION_RESET || OVERRIDE_FIELD_DELETED ||
    // EXPRESSION_LIST_ITEM_REMOVED || EXPRESSION_LIST_ITEM_MOVED. These actions
    // should never be called from a holey record, you can not have anything to
    // delete/reset/reorder on a record that is a hole, that just does not add up.
    forwardDispatch(dispatch)({in: 'Record', at: key})(action);
  }
};

const isDisabledByDynamoBindingFromFullExpressionPath = (
  dynBoundFieldPaths: BindingPathMapPojo | null | undefined,
  exprPath: ExpressionPath,
  value: any
): boolean => {
  if (!dynBoundFieldPaths) return false;

  if (exprPath.length < 1 || exprPath.some((part) => !part)) {
    return false;
  }

  return isDisabledByDynamoBinding(dynBoundFieldPaths, exprPath, value);
};

const getBindUIVariant = ({
  dataType,
  dataTypeImpl,
}: {
  dataType: Type | null | undefined;
  dataTypeImpl: TypeImplementation.TypeImplementation | null | undefined;
}): BindUIVariant => {
  const system = dataType ? getSystem(dataType) : null;

  const typeImplBindUIVariant = dataTypeImpl
    ? TypeImplementation.getBindUIVariant(dataTypeImpl)
    : null;
  if (typeImplBindUIVariant) {
    return typeImplBindUIVariant;
  }

  return system?.bindUIVariant || 'dot';
};

const canRenderBindingVariantLabel = (
  bindUIVariant: BindUIVariant,
  expression: ContextualExpression
): boolean =>
  bindUIVariant === 'label' &&
  !ContextualExpressions.isBinding(stripAnn(expression));

/**
 * Render a full width label if the following conditions are met:
 * - Type system information has `isFullWidth` set
 * - We are not in override context (overrides already have a separate top level label)
 *   - or we're in the logic context
 * - The current expression is not bound (the binding dropdown will be on the same row as the label)
 * - If the `bindUIVariant` is not button (the button bindUIVariants we want on the same row as the label)
 */
const isFullWidthLabel = ({
  dataTypeImpl,
  dataType,
  context,
  expression,
}: {
  dataTypeImpl: TypeImplementation.TypeImplementation | null | undefined;
  dataType: Type | null | undefined;
  context: TypeImplementation.DataTypeEnvironmentContext;
  expression: ContextualExpression;
}): boolean => {
  const dataTypeIsFullWidth =
    (dataTypeImpl && TypeImplementation.getIsFullWidth(dataTypeImpl)) ||
    (dataType && getSystem(dataType)?.isFullWidth);

  if (context === 'logic') {
    return true;
  }

  return Boolean(
    dataTypeIsFullWidth &&
    context !== 'override' &&
    !ContextualExpressions.isBinding(stripAnn(expression))
  );
};

/*
 * UI components and styles
 */

const flex = {display: 'flex'} as const;
const flexItemOne = {flex: 1} as const;

const fullWidth = {gridColumn: '1 / -1', position: 'relative'} as const;
const FullWidth = styledDiv(fullWidth);

const StyledHintText = styledDiv(({theme: {colors, fontSizes}}) => ({
  color: colors.foregroundDim,
  fontSize: fontSizes.copy,
  alignItems: 'center',
  display: 'grid',
  marginBottom: '10px',
  marginTop: '-8px',
  letterSpacing: -0.2,
}));

const TwoColumns = styledDiv(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: `${rightSidebarLabelWidth}px minmax(0, auto)`,
  gridGap: `${theme.gutters.normal}px`,
  alignItems: 'center',
}));

const Separator = emotionStyled('div')({
  width: '100%',
  height: tokens.space[2],
});

const LabelSeparator = emotionStyled('div')({
  width: '100%',
  height: '$1',
});

const Divider = styledDiv(({theme}) => ({
  height: 1,
  backgroundColor: theme.colors.backgroundDeep,
  width: `calc(100% + ${theme.gutters.x8 * 2}px)`,
  marginLeft: -theme.gutters.x8,
  gridColumn: '1 / -1',
  marginBottom: `${theme.gutters.normal}px`,
}));

const divider = <Divider />;

const Wrapper = emotionStyled('div')({
  padding: tokens.space[2],
  width: rightSidebarWidth,
});

const LabelContainer = styled(HStack)({alignSelf: 'start', gap: '0'});

const ExpressionDataTypeMismatchError = () => (
  <Note colorVariant="default" hasIcon={false}>
    {process.env.NODE_ENV === 'development'
      ? 'The Expression & Type are incompatible.'
      : 'Settings Error, please contact customer support!'}
  </Note>
);

const bindingAndExpressionDisabledState = {
  binding: true,
  expression: () => true,
  reason:
    'This is disabled because the element is already linked to a Collection Field.',
} as const;

type BindUIVariant = 'dot' | 'custom' | 'label';

type SettingsProps = {
  expression: ContextualExpression;
  dataType: Type;
  dispatch: ExpressionEditorDispatch;
  onBindingChange: (
    expression: null | Expression,
    meta: ExpressionBindMetaPayload
  ) => void;
  bindUIVariant: BindUIVariant;
  automationLabel: string;
  label?: string | null;
  dataTypeImpl: null | TypeImplementation.TypeImplementation;
  plugins: Plugins.PluginsType;
  showDivider?: boolean;
  uiFlags: TypeImplementation.DataTypeEnvironment['uiFlags'];
};

type EEProps = {
  dispatch: ExpressionEditorDispatch;
  expression: ContextualExpression;
  label?: string | null;
};

const CustomTooltipContent = ({label}: {label: string}) => {
  return <Text whiteSpace="pre-line">{label}</Text>;
};

const FieldTooltip = React.memo(
  ({label, tooltipId}: {label: string; tooltipId: string}) => {
    return (
      <Tooltip label={<CustomTooltipContent label={label} />}>
        <View color="$textSecondary" _hover={{color: '$textPrimary'}}>
          <HelpCircleIcon
            data-automation-id={`tooltip_${tooltipId}`}
            aria-label={`Tooltip for ${tooltipId}`}
          />
        </View>
      </Tooltip>
    );
  }
);

/*
 *************************************
 * Main ExpressionEditor entry point *
 *************************************
 */
export const ExpressionEditor = ({expression, dispatch, label}: EEProps) => {
  const plugins = usePlugins();
  const [checkerState] = React.useContext(TypeCheckerState);
  const {env} = useExpressionEditorImplementationContext();
  const focusContext = React.useContext(ExpressionEditorFocusContext);
  const fieldWrapperRef = React.useRef<HTMLDivElement>(null);
  const ann = getAnn(expression);
  const expressionPath = ann.env.expressionPath;
  const focusRequest = focusContext.getFocusRequest?.();

  React.useLayoutEffect(() => {
    if (
      !focusRequest ||
      !Expressions.pathsEqual(focusRequest.expressionPath, expressionPath)
    ) {
      return;
    }

    const field =
      fieldWrapperRef.current?.querySelector('input') ??
      fieldWrapperRef.current?.querySelector('textarea');
    if (
      !(field instanceof HTMLInputElement) &&
      !(field instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    field.focus();
    if (focusRequest.caretOffset !== null) {
      field.setSelectionRange(
        focusRequest.caretOffset,
        focusRequest.caretOffset
      );
    }
    focusContext.onFocusResolved?.();
  }, [expressionPath, focusContext, focusRequest]);

  const onFieldFocusCapture = (event: React.FocusEvent<HTMLDivElement>) => {
    const field = event.target;
    if (
      !(field instanceof HTMLInputElement) &&
      !(field instanceof HTMLTextAreaElement)
    ) {
      return;
    }

    focusContext.onFieldFocused?.(() => {
      if (document.activeElement !== field) return undefined;

      return {
        expressionPath,
        caretOffset: field.selectionStart,
      };
    });
  };
  const strippedExpr = stripAnn(expression);
  const typed = ContextualExpressions.getTyped(strippedExpr);

  // TODO: We need to allow editing of types instead of ignoring Typed
  if (typed)
    return (
      <ExpressionEditor
        expression={typed.expr}
        dispatch={dispatch}
        label={label}
      />
    );

  // Expose the top-level record field key (only when this ExpressionEditor is
  // rendering a direct field of its owning Record) so the containing panel can
  // locate the wrapper in the DOM — for example, to scroll the field that is
  // bound to the currently selected canvas element into view.
  const topLevelFieldKey =
    expressionPath.length === 1 && expressionPath[0]?.in === 'Record'
      ? expressionPath[0].at
      : undefined;

  // Single source of truth for the leaf "does this render?" cases (missing
  // type / private / hidden), shared with `Record` so its field bookkeeping
  // agrees with what the editor renders. The `Typed` recursion above is kept
  // intentionally — delegating it to the helper would change how Typed wrappers
  // render (they re-render with the unwrapped inner expression today).
  const resolved = resolveRenderedField(expression, checkerState, plugins, env);
  if (resolved === null) return null;

  const {dataType} = resolved;

  const isListOfElements = Types.isTypeListOfElements(dataType);
  const dataTypeImpl = isListOfElements
    ? __DEPRECATED__ChildrenPrimitiveType
    : Types.getImplementation(dataType, plugins);
  const bindUIVariant = getBindUIVariant({
    dataType,
    dataTypeImpl,
  });

  const onBindingChange = (
    updatedBinding: null | Expression,
    meta: ExpressionBindMetaPayload
  ) => {
    dispatch({
      type: 'EXPRESSION_BIND_CHANGED',
      binding: updatedBinding,
      meta,
      key: emptyArray,
    });
  };

  const disabledByDynamoBinding =
    isDisabledByDynamoBindingFromFullExpressionPath(
      env.dynBoundFieldPaths,
      ann.env.expressionPath,
      ContextualExpressions.getStaticValue(strippedExpr)
    );

  const system = getSystem(dataType);
  const nextLabel = getNextLabel(dataType, label);

  const uiFlags = Object.assign({}, env.uiFlags);

  if (disabledByDynamoBinding) {
    uiFlags.disabled = bindingAndExpressionDisabledState;
  }

  const fieldSettingsProps = {
    dataType,
    dataTypeImpl,
    label: nextLabel,
    expression,
    dispatch,
    bindUIVariant,
    onBindingChange,
    plugins,
    automationLabel: nextLabel || '',
    uiFlags,
  } as const;

  const dataTypeImplLabel = dataTypeImpl
    ? TypeImplementation.getLabel(dataTypeImpl)
    : '';

  /**
   * When in override context, first conditionally render out
   * an override row before our "normal" Label and Settings pair.
   *
   * Property labels here show the prop name only (no WFDL type icons).
   * Type icons remain on the component Props tab when defining properties.
   */
  if (env.context === 'override') {
    const expressionBindingProps = {
      expression,
      onChange: fieldSettingsProps.onBindingChange,
      bindUIVariant,
      automationLabel: fieldSettingsProps.automationLabel,
      uiFlags: fieldSettingsProps.uiFlags,
    };
    const Space = LabelSeparator;
    return (
      <div
        ref={fieldWrapperRef}
        data-automation-id={fieldWrapperLabel(nextLabel)}
        data-field-key={topLevelFieldKey}
        onFocusCapture={onFieldFocusCapture}
      >
        {/* If we have a label, we only want the top level
         *   overrides to show the extra label, not things like function
         *   arguments.
         *
         * -> render an override label row */}
        {nextLabel &&
        ann.env.expressionPath.length === 1 &&
        system?.labelVariant !== 'hidden' ? (
          <>
            <VStack>
              <LabelContainer>
                {canRenderBindingVariantLabel(bindUIVariant, expression) ? (
                  <ExpressionBinding {...expressionBindingProps}>
                    <HStack spacing={1}>
                      <FieldLabel
                        label={nextLabel}
                        expression={fieldSettingsProps.expression}
                        dispatch={fieldSettingsProps.dispatch}
                      />
                      {system?.toolTipText && (
                        <FieldTooltip
                          label={system.toolTipText}
                          tooltipId={nextLabel}
                        />
                      )}
                    </HStack>
                  </ExpressionBinding>
                ) : (
                  <HStack spacing={1}>
                    <FieldLabel
                      label={nextLabel}
                      expression={fieldSettingsProps.expression}
                      dispatch={fieldSettingsProps.dispatch}
                    />
                    {system?.toolTipText && (
                      <FieldTooltip
                        label={system.toolTipText}
                        tooltipId={nextLabel}
                      />
                    )}
                  </HStack>
                )}
              </LabelContainer>
            </VStack>
            <Space
              data-automation-id={`expression-editor-override-label-separator_${nextLabel}`}
            />
          </>
        ) : null}

        <LabelAndSettingsAndBinding
          fieldSettingsProps={{
            ...fieldSettingsProps,
            label: dataTypeImplLabel,
          }}
        />
      </div>
    );
  } else {
    /*
     * Notice that Settings is encompassed by a Binding component.
     */
    return (
      <div
        ref={fieldWrapperRef}
        className="w-ExpressionEditor-fieldWrapper"
        data-automation-id={fieldWrapperLabel(nextLabel)}
        data-field-key={topLevelFieldKey}
        onFocusCapture={onFieldFocusCapture}
      >
        <LabelAndSettingsAndBinding fieldSettingsProps={fieldSettingsProps} />
      </div>
    );
  }
};

/**
 * Field label and settings and binding controls row
 */
const LabelAndSettingsAndBinding = ({
  fieldSettingsProps,
}: {
  fieldSettingsProps: SettingsProps;
}) => {
  const eeContext = useExpressionEditorContext();
  const {dataType, dataTypeImpl, label} = fieldSettingsProps;
  const isListOfElements = Types.isTypeListOfElements(dataType);
  const itemTy = Types.getListItemType(dataType);
  const recordFields = Types.getRecordFields(dataType);
  const unionValues = Types.getUnionValues(dataType);
  const isRecordOrUnionType = recordFields !== null || unionValues !== null;
  const system = dataType ? getSystem(dataType) : null;
  const dataTypeImplLabelVariant =
    dataTypeImpl && TypeImplementation.getLabelVariant(dataTypeImpl);
  const hideLabel =
    dataTypeImplLabelVariant === 'hidden' || system?.labelVariant === 'hidden';
  const uiFlags = fieldSettingsProps.uiFlags;
  const isOverrideContext = eeContext === 'override';
  const labelWidth = system?.labelWidth;

  /*
   * Don't render a Label for Record or Union
   * or if we don't have a label
   * or if the Type has hideLabel set to true
   */
  if (isRecordOrUnionType || !label || hideLabel) {
    return (
      <FullWidth>
        <SettingsAndBinding fieldSettingsProps={fieldSettingsProps} />
      </FullWidth>
    );
  } else if (
    /*
     * Render Label on its own row if...
     *
     * the type is a List
     */
    (itemTy && !isListOfElements) ||
    /*
     * _or_ the underlying Type renders out a full width label
     */
    isFullWidthLabel({
      dataTypeImpl,
      dataType,
      context: eeContext,
      expression: fieldSettingsProps.expression,
    })
  ) {
    const {expression, bindUIVariant} = fieldSettingsProps;
    const expressionBindingProps = {
      expression,
      onChange: fieldSettingsProps.onBindingChange,
      bindUIVariant,
      automationLabel: fieldSettingsProps.automationLabel,
      uiFlags,
    } as const;

    return (
      <>
        <VStack>
          <LabelContainer>
            {canRenderBindingVariantLabel(bindUIVariant, expression) ? (
              <ExpressionBinding {...expressionBindingProps}>
                <FieldLabel
                  label={label}
                  expression={fieldSettingsProps.expression}
                  dispatch={fieldSettingsProps.dispatch}
                />
              </ExpressionBinding>
            ) : (
              <FieldLabel
                label={label}
                expression={fieldSettingsProps.expression}
                dispatch={fieldSettingsProps.dispatch}
              />
            )}
            {system?.toolTipText && (
              <Tooltip label={system?.toolTipText}>
                <HelpOutlineIcon data-automation-id={`tooltip_${label}`} />
              </Tooltip>
            )}
          </LabelContainer>
          {system?.hintText ? (
            <StyledHintText data-automation-id={`hint-text_${label}`}>
              {system.hintText}
            </StyledHintText>
          ) : null}
        </VStack>
        <FullWidth>
          <SettingsAndBinding fieldSettingsProps={fieldSettingsProps} />
        </FullWidth>
      </>
    );
  } else {
    /*
     * Render a Label and Settings pair or just Settings if
     * isOverrideContext
     */
    return isOverrideContext ? (
      <FullWidth>
        <SettingsAndBinding fieldSettingsProps={fieldSettingsProps} />
      </FullWidth>
    ) : (
      <TwoColumns>
        <LabelContainer>
          <FieldLabel
            label={label}
            labelWidth={labelWidth}
            expression={fieldSettingsProps.expression}
            dispatch={fieldSettingsProps.dispatch}
            // In override context we already render a label above this one
            isSecondLabel={isOverrideContext}
          />
        </LabelContainer>
        <SettingsAndBinding fieldSettingsProps={fieldSettingsProps} />
      </TwoColumns>
    );
  }
};

/**
 * Field settings and binding controls
 */
const SettingsAndBinding = ({
  fieldSettingsProps,
}: {
  fieldSettingsProps: SettingsProps;
}) => {
  const eeContext = useExpressionEditorContext();
  const {bindUIVariant, dispatch, expression, dataType, label, plugins} =
    fieldSettingsProps;
  /** This is a very manual override for the Text fields rendered when the context of
  the ExpressionEditor is for Logic Flows. We are overriding an `TText` type field to not use
  the default implementation of `TText`, but to instead use our InterpolatedTextUI component
  since nearly all text fields in Logic Flows need to be interpolated text
  This override will be removed once the necessary API's are available https://webflow.atlassian.net/browse/LOGIC-3305 */
  const typeConstructorName = Types.getTypeConstructor(dataType);
  if (
    typeConstructorName &&
    namesEqual(typeConstructorName, textTypeName) &&
    eeContext === 'logic' &&
    getSystem(dataType)?.isBindable !== false
  ) {
    const meta = getMeta(dataType);
    const onInterpolatedExpressionChange = (newExpression: Expression) => {
      return dispatch({
        key: emptyArray,
        type: 'EXPRESSION_ACTION',
        payload: {
          newExpression,
        },
      });
    };
    return (
      <InterpolatedText
        expr={expression}
        onExpressionChange={onInterpolatedExpressionChange}
        placeholder={(meta && meta.placeholder) || ''}
        multiLine={(meta && meta.multiLine) || false}
        label={label || ''}
      />
    );
  }

  const bindingTypeImpl = getPreferredTypeImplementationForBinding(
    expression,
    plugins
  );
  if (bindingTypeImpl) {
    return <Settings {...fieldSettingsProps} dataTypeImpl={bindingTypeImpl} />;
  }

  if (
    bindUIVariant === 'custom' ||
    canRenderBindingVariantLabel(bindUIVariant, expression)
  ) {
    return <Settings {...fieldSettingsProps} />;
  }
  const expressionBindingProps = {
    expression: fieldSettingsProps.expression,
    onChange: fieldSettingsProps.onBindingChange,
    bindUIVariant,
    automationLabel: fieldSettingsProps.automationLabel,
    uiFlags: fieldSettingsProps.uiFlags,
  } as const;

  /*
   * Render the Binding element separately for a `Call`
   */
  const {fun, args} = ContextualExpressions.deconstructCall(
    fieldSettingsProps.expression
  );
  const argumentSettings = ArgumentSettings({
    fun,
    dispatch,
    label: getBindingLabel(getAnn(fun).env.context.values, stripAll(fun)),
    args,
  });
  if (argumentSettings !== null) {
    return (
      <div style={flex}>
        <ExpressionBinding {...expressionBindingProps} style={flexItemOne} />
        {argumentSettings}
      </div>
    );
  }
  /*
   * Render the Binding element wrapping the actual Settings UI
   */
  return (
    <ExpressionBinding {...expressionBindingProps}>
      <Settings {...fieldSettingsProps} />
    </ExpressionBinding>
  );
};

/**
 * Field settings component, renders out the `typeImplementation.render` for
 * leaf types or further Composite renders for `Record`, `Union`, `List`, `Call`
 */
const Settings = ({
  expression,
  dataType,
  dataTypeImpl,
  dispatch,
  plugins,
  showDivider,
}: SettingsProps) => {
  /*
   * Alias Type
   */
  if (
    dataTypeImpl &&
    dataTypeImpl instanceof TypeImplementation.AliasTypeImplementation
  ) {
    const aliasDispatch: ExpressionEditorDispatch = (action) => {
      return dispatch({
        ...action,
        key: action.key || emptyArray,
      });
    };
    // Unwrap the alias type so that the Alias render has the type information behind the Alias layer in it.
    const unwrappedAliasExpr = setType(
      expression,
      unwrapAliasTypeWithSystemAndMeta(dataType, plugins)
    );
    const AliasRender = dataTypeImpl.render;
    return <AliasRender expr={unwrappedAliasExpr} dispatch={aliasDispatch} />;
  }
  /*
   * Unique Type
   */
  if (
    dataTypeImpl &&
    dataTypeImpl instanceof TypeImplementation.UniqueTypeImplementation
  ) {
    const UniqueRender = dataTypeImpl.render;

    // If we are rendering a binding don't unwrap anything.
    if (ContextualExpressions.isBinding(stripAnn(expression))) {
      return <UniqueRender expr={expression} dispatch={dispatch} />;
    }
    const uniqueDispatch: ExpressionEditorDispatch = (action) => {
      // If we are adding a top level binding to the Distinct, we can just use
      // the action as is.
      if (
        action.key.length === 0 &&
        action.type === 'EXPRESSION_BIND_CHANGED'
      ) {
        return dispatch(action);
      }

      const newKey = inDistinct.concat(action.key || emptyArray);
      return dispatch({
        ...action,
        key: newKey,
      });
    };

    const innerExpr = ContextualExpressions.getIn(expression, inDistinct);
    if (!innerExpr) {
      throw new Error(
        `Could not find inner expression for unique type ${Types.print(
          dataType
        )}`
      );
    }
    return <UniqueRender expr={innerExpr} dispatch={uniqueDispatch} />;
  }
  /*
   * Record
   */
  const fields = Types.getRecordFields(dataType);
  if (fields) {
    return (
      <Record
        dispatch={dispatch}
        expression={expression}
        fields={fields}
        showDivider={Boolean(showDivider)}
      />
    );
  }
  /*
   * Union
   */
  const tyApp = Types.deconstructTypeApplication(dataType);
  const items = Types.getUnionValues(dataType);
  if (items && tyApp) {
    return (
      <Union
        dispatch={dispatch}
        expression={expression}
        dataType={dataType}
        plugins={plugins}
        items={items}
        tyApp={tyApp}
      />
    );
  }
  /*
   * List
   */
  const isListOfElements = Types.isTypeListOfElements(dataType);
  const itemTy = Types.getListItemType(dataType);
  if (itemTy && !isListOfElements) {
    return (
      <List
        dispatch={dispatch}
        expression={expression}
        plugins={plugins}
        itemTy={itemTy}
        sortable={Boolean(getMeta(dataType)?.sortable)}
      />
    );
  }
  /*
   * "Custom" Types
   */
  const enumValues = Types.getEnumValues(dataType);
  if (enumValues || tyApp || isListOfElements) {
    if (!dataTypeImpl) {
      throw new Error(
        `Could not determine DataTypeImplementation for ${Types.print(
          dataType
        )}`
      );
    }

    if (
      !(dataTypeImpl instanceof TypeImplementation.CustomTypeImplementation)
    ) {
      throw new Error(
        `Found a non-custom type implementation for custom: ${Types.print(
          dataType
        )}`
      );
    }

    const settingsDispatch: ExpressionEditorDispatch = (action) => {
      const newExpression =
        action.type === 'EXPRESSION_ACTION' && action.payload.newExpression
          ? action.payload.newExpression
          : null;

      if (newExpression === stripAll(expression)) {
        // Should we still dispatch here?
        return;
      }

      return dispatch({
        ...action,
        key: action.key || emptyArray,
      });
    };

    const CustomRender = dataTypeImpl.render;
    return <CustomRender expr={expression} dispatch={settingsDispatch} />;
  }

  /*
   * Unknown type
   *
   * If we have a Hole of an Unknown type the user is free to fill that hole
   * with absolutely anything.
   */
  if (
    Types.getUnknownType(dataType) &&
    ContextualExpressions.getHole(stripAnn(expression))
  ) {
    const renderPopoverContent = () => (
      <Wrapper>
        <TypeEditor
          type={undefined}
          onChange={(type) => {
            dispatch({
              key: emptyArray,
              type: 'EXPRESSION_ACTION',
              payload: {
                newExpression: ETyped({
                  type,
                  expr: EHole(),
                }),
              },
            });
          }}
        />
      </Wrapper>
    );

    return (
      <DeprecatedPopoverBridge
        renderPopoverContent={renderPopoverContent}
        showMask="TRANSPARENT"
        metricsIdentifier="expression-editor-settings-popover"
      >
        {({Anchor, openPopover}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <Button
                _ref={ref}
                data-automation-id="choose-type"
                style={{height: BINDING_SELECT_BUTTON_HEIGHT}}
                onClick={openPopover}
              >
                Choose a value type
              </Button>
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    );
  }

  // Unsupported
  return null;
};

const topBottomMargin = {
  marginTop: '5px',
  marginBottom: '5px',
} as const;

function PrimaryLabel({
  label,
  labelWidth,
  expression,
  dispatch,
}: {
  label: string;
  labelWidth?: LabelWidth;
  expression: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
}) {
  const {
    env: {context},
  } = useExpressionEditorImplementationContext();
  const items = useLabelItems(expression, dispatch, context);

  return (
    <SettingsPanelPropertyLabel
      labelWidth={labelWidth}
      items={items}
      data-automation-id={`Type--Label_${label || '<Unknown>'}`}
      style={topBottomMargin}
    >
      {label}
    </SettingsPanelPropertyLabel>
  );
}

function SecondaryLabel({label}: {label: string}) {
  const emptyItems = React.useMemo(() => [], []);
  return (
    <SettingsPanelPropertyLabel
      items={emptyItems}
      data-automation-id="symbols-dataType-contentType"
      style={topBottomMargin}
    >
      {label}
    </SettingsPanelPropertyLabel>
  );
}

type LabelWidth = 'narrow' | 'narrowest';

const FieldLabel = ({
  label,
  labelWidth,
  expression,
  dispatch,
  isSecondLabel = false,
}: {
  label: string;
  labelWidth?: LabelWidth;
  expression: ContextualExpression;
  dispatch: ExpressionEditorDispatch;
  isSecondLabel?: boolean;
}) => {
  if (isSecondLabel) {
    return <SecondaryLabel label={label} />;
  }
  return (
    <PrimaryLabel
      label={label}
      labelWidth={labelWidth}
      expression={expression}
      dispatch={dispatch}
    />
  );
};

type RenderedField = {expression: ContextualExpression; dataType: Type};

/**
 * Resolves what `ExpressionEditor` actually renders for an expression, or
 * `null` when it renders nothing: a `Typed` wrapper resolves to its inner
 * expression, then a missing type, a private type, or the active
 * `uiFlags.hidden` filter each render nothing.
 *
 * Single source of truth for the leaf "does this render?" cases. Used by
 * `ExpressionEditor`'s early return (to decide whether to render) and by
 * `Record` (via `shouldHideRecordField`) to decide which fields show — so a
 * field that renders nothing doesn't leave a sibling separator behind, which
 * would appear as an empty vertical gap in the panel.
 */
const resolveRenderedField = (
  expression: ContextualExpression,
  checkerState: CheckerState,
  plugins: Plugins.PluginsType,
  env: TypeImplementation.DataTypeEnvironment
): RenderedField | null => {
  const typed = ContextualExpressions.getTyped(stripAnn(expression));
  if (typed) {
    return resolveRenderedField(typed.expr, checkerState, plugins, env);
  }
  const rawDataType = getAnn(expression).type;
  if (rawDataType === null) return null;
  const dataType = substituteType(getSubstitution(checkerState))(rawDataType);
  if (Types.isPrivate(dataType, plugins, stripAll(expression))) return null;
  if (env.uiFlags?.hidden?.expression?.(expression)) return null;
  return {expression, dataType};
};

/**
 * Record-only: whether a single field should be hidden — an unlinked/orphaned
 * override field, a field the editor renders nothing for (via
 * `resolveRenderedField`), or (in default context) a composite whose own fields
 * would all hide. `Record` asks this instead of re-deriving the leaf rules.
 */
const shouldHideRecordField = (
  fieldExpression: ContextualExpression,
  fieldType: Type | undefined,
  checkerState: CheckerState,
  plugins: Plugins.PluginsType,
  env: TypeImplementation.DataTypeEnvironment
): boolean => {
  // Record-only: unlinked / orphaned fields aren't shown in a component instance.
  if (env.context === 'override') {
    // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
    if (getSystem(fieldType)?.isUnlinked) return true;
  }

  const rendered = resolveRenderedField(
    fieldExpression,
    checkerState,
    plugins,
    env
  );
  if (rendered === null) return true;

  // Record-only: collapse a composite whose rows would all hide — e.g. a
  // non-content composite whose children are all filtered out in edit site
  // mode — so the parent doesn't keep a separator for an otherwise-empty row.
  // Scoped to `default` context: the content filter that empties composites
  // only runs there (ElementSettingsExpressionEditorProps gates
  // `uiFlags.hidden.expression` on `envContext === 'default'`), and other
  // contexts may rely on still rendering a wrapper for an otherwise-empty
  // composite (e.g. override's top-level label row).
  return (
    env.context === 'default' &&
    shouldCollapseRecordField(
      rendered.expression,
      rendered.dataType,
      checkerState,
      plugins,
      env
    )
  );
};

/**
 * Record-only: whether a composite rendered through the generic `Record`
 * produces no output because every one of its fields would hide. Alias/Unique
 * types have their own renderers and holey records render an editor per field,
 * so neither is ever empty.
 */
const shouldCollapseRecordField = (
  expression: ContextualExpression,
  dataType: Type,
  checkerState: CheckerState,
  plugins: Plugins.PluginsType,
  env: TypeImplementation.DataTypeEnvironment
): boolean => {
  const dataTypeImpl = Types.getImplementation(dataType, plugins);
  if (
    dataTypeImpl instanceof TypeImplementation.AliasTypeImplementation ||
    dataTypeImpl instanceof TypeImplementation.UniqueTypeImplementation
  ) {
    return false;
  }
  const fields = Types.getRecordFields(dataType);
  if (!fields) return false;
  const fieldKeys = Object.keys(fields);
  if (fieldKeys.length === 0) return false;

  const record = ContextualExpressions.getRecord(stripAnn(expression));
  if (!record) return false;

  return fieldKeys.every(
    (key) =>
      record[key] == null ||
      shouldHideRecordField(
        record[key],
        fields[key],
        checkerState,
        plugins,
        env
      )
  );
};

/**
 * Composite Types render helpers
 */
const Record = ({
  expression,
  dispatch,
  fields,
  showDivider,
}: {
  dispatch: ExpressionEditorDispatch;
  expression: ContextualExpression;
  fields: {
    [key: string]: Type;
  };
  showDivider: boolean;
}) => {
  const typeCheckerStateContext = React.useContext(TypeCheckerState);
  const [rootCheckerState, rootContextualExpr] = typeCheckerStateContext;
  const {env} = useExpressionEditorImplementationContext();

  const plugins = usePlugins();
  const record = ContextualExpressions.getRecord(stripAnn(expression));
  const hole = ContextualExpressions.getHole(stripAnn(expression));
  // If we have something other than Hole we don't know what to render. (yet, in
  // the future we want to expression to solely drive rendering)
  if (!record && !hole) {
    return <ExpressionDataTypeMismatchError />;
  }
  const orderedFields = Object.keys(fields);

  const buildFieldExpression = (key: string) =>
    record
      ? record[key]
      : updateAnn(
          (ann) => ({
            env: {
              ...ann.env,
              expressionPath: ann.env.expressionPath.concat([
                {
                  in: 'Record',
                  at: key,
                },
              ]),
            },
            // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type | null'.
            type: fields[key],
            errors: ann.errors,
          }),
          expression
        );

  // Compute the fields that actually render up front so the separators and
  // `isLast` below are derived only from them. Otherwise a field whose editor
  // renders nothing (private, type-less, hidden by the active uiFlags filter,
  // or an empty composite) still leaves its sibling separator behind, creating
  // empty vertical gaps in the panel.
  const visibleFields = orderedFields.reduce<
    Array<{key: string; fieldExpression: ContextualExpression | undefined}>
  >((acc, key) => {
    const fieldExpression = buildFieldExpression(key);
    if (fieldExpression == null) return acc;
    if (
      !shouldHideRecordField(
        fieldExpression,
        fields[key],
        rootCheckerState,
        plugins,
        env
      )
    ) {
      acc.push({key, fieldExpression});
    }
    return acc;
  }, []);

  const renderedFields = visibleFields.map(({key, fieldExpression}, index) => {
    const isLast = visibleFields.length - 1 === index;
    const fieldDataType = fields[key];
    const label = getLabelForType(
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      fieldDataType,
      // @ts-expect-error - TS2345 - Argument of type 'ContextualExpression | undefined' is not assignable to parameter of type 'AnnotatedExpressionF<ContextualExpressionAnnotation, ContextualExpression>'.
      getAnn(fieldExpression).env.expressionPath
    );
    const fieldDispatch: ExpressionEditorDispatch = (action) => {
      // If the top level is a hole then we need to work around it
      if (hole) {
        // Create a new expression with holes for fields, use the
        // `newExpression` from the action as the targeted field
        holeRecordFieldDispatch(action, orderedFields, key, dispatch);
      } else {
        // If we don't have a hole we can just call dispatch normally
        forwardDispatch(dispatch)({in: 'Record', at: key})(action);
      }
    };
    return (
      <React.Fragment key={key}>
        <ExpressionEditor
          dispatch={fieldDispatch}
          // @ts-expect-error - TS2322 - Type 'ContextualExpression | undefined' is not assignable to type 'ContextualExpression'.
          expression={fieldExpression}
          label={label}
        />
        {!isLast ? (
          <Separator
            data-automation-id={`expression-editor-separator-record_${label}`}
          />
        ) : null}
        {!isLast && showDivider ? divider : null}
      </React.Fragment>
    );
  });
  if (hole) {
    const currPath = getAnn(expression).env.expressionPath;

    const holeyRecordFieldsWithAnn = orderedFields.reduce<
      Record<string, ContextualExpression>
    >((acc, curr) => {
      const fieldDataType = fields[curr];
      acc[curr] = updateAnn(
        (ann) => ({
          env: {
            ...ann.env,
            expressionPath: ann.env.expressionPath.concat([
              {
                in: 'Record',
                at: curr,
              },
            ]),
          },
          // @ts-expect-error - TS2322 - Type 'Type | undefined' is not assignable to type 'Type | null'.
          type: fieldDataType,
          errors: ann.errors,
        }),
        expression
      );
      return acc;
    }, {});

    const filledHoleyContextualExpr = updateExpr((_) =>
      ERecord(holeyRecordFieldsWithAnn)
    )(expression);

    const nextFullExpression = rootContextualExpr
      ? ContextualExpressions.setIn(
          rootContextualExpr,
          currPath,
          filledHoleyContextualExpr
        )
      : null;

    return (
      <TypeCheckerState.Provider value={[rootCheckerState, nextFullExpression]}>
        {renderedFields}
      </TypeCheckerState.Provider>
    );
  }

  return (
    <TypeCheckerState.Provider value={typeCheckerStateContext}>
      {renderedFields}
    </TypeCheckerState.Provider>
  );
};

const Union = ({
  expression,
  dispatch,
  dataType,
  plugins,
  tyApp,
  items,
}: {
  dispatch: ExpressionEditorDispatch;
  expression: ContextualExpression;
  dataType: Type;
  plugins: Plugins.PluginsType;
  tyApp: {
    args: Array<Type>;
    con: Qualified<TypeName>;
  };
  items: {
    [key: string]: Type;
  };
}) => {
  const union = ContextualExpressions.getUnion(stripAnn(expression));
  const context = useExpressionEditorContext();
  const {expressionPath} = getAnn(expression).env;
  const unionDispatch = (action: {payload: string; type: 'TAG_CHANGED'}) => {
    const state = union;
    const reducer = makeUnionReducer(context)(plugins)(dataType);
    const newState = reducer(action);

    if (newState === null) {
      // Should we still dispatch here?
      return;
    }

    return dispatch({
      key: emptyArray,
      type: 'EXPRESSION_ACTION',
      payload: {
        newExpression: EUnion(newState),

        // We can use these for analytics purposes if we need:
        meta: {
          previousState: state,
          action,
        },
      },
    });
  };

  const enumDispatch =
    (prevVal: string | null) =>
    (action: TypeImplementation.ExpressionEditorAction): void => {
      if (action.type === 'EXPRESSION_ACTION') {
        const {newExpression} = action.payload;
        const newVal = Expressions.getEnum(newExpression);
        if (newVal && newVal !== prevVal) {
          unionDispatch({
            type: 'TAG_CHANGED',
            payload: newVal,
          });
        }
      }
    };

  const enumTy = withSystem(
    getSystem(dataType) || {},
    withMeta(
      {
        // We always force `Union` to not have live preview while
        // hovering over options in the dropdown:
        ...getMeta(dataType),
        withoutLivePreview: true,
      },
      // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
      enumOf(tyApp.args[0])
    )
  );
  // We might have a `Hole` for the Union, render the Enum type with that Hole
  // in place
  if (ContextualExpressions.getHole(stripAnn(expression))) {
    return (
      <ExpressionEditor
        dispatch={enumDispatch(null)}
        expression={setType(expression, enumTy)}
        label={getLabelForType(dataType, expressionPath)}
      />
    );
  }
  if (!union) {
    return <ExpressionDataTypeMismatchError />;
  }
  const deepStrippedUnionValueExpr = stripAll(union.value);
  const {tag} = union;
  const unionValueDataType = items[tag];
  const privateUnionValue = Types.isPrivate(
    unionValueDataType,
    plugins,
    deepStrippedUnionValueExpr
  );
  return (
    <>
      <ExpressionEditor
        dispatch={enumDispatch(tag)}
        expression={annotate(EEnum(union.tag), {
          type: enumTy,
          env: getAnn(expression).env,
          errors: emptyArray,
        })}
        label={getLabelForType(dataType, expressionPath)}
      />
      {privateUnionValue ? null : (
        <>
          <Separator data-automation-id="expression-editor-separator-union" />
          <ExpressionEditor
            dispatch={forwardDispatch(dispatch)({in: 'Union', tag})}
            expression={union.value}
            label={
              unionValueDataType
                ? getLabelForType(unionValueDataType, [
                    ...expressionPath,
                    {in: 'Union', tag},
                  ])
                : ''
            }
          />
        </>
      )}
    </>
  );
};

const List = ({
  expression,
  dispatch,
  plugins,
  itemTy,
  sortable,
}: {
  dispatch: ExpressionEditorDispatch;
  expression: ContextualExpression;
  plugins: Plugins.PluginsType;
  itemTy: Type;
  sortable: boolean;
}) => {
  const {
    env: {context},
  } = useExpressionEditorImplementationContext();
  const listActions = useListActions(expression, dispatch, context);
  const state =
    ContextualExpressions.getList(stripAnn(expression)) ||
    (emptyArray as Array<ContextualExpression>);
  return (
    <ListRenderComponent
      itemType={itemTy}
      plugins={plugins}
      dispatch={dispatch}
      items={state}
      sortable={sortable}
      listActions={listActions}
    />
  );
};

// HACK: These styles shouldn't be necessary once we figure out a
// better layout system for DataTypes
const listHeaderActionsStyle = {
  position: 'absolute',
  right: 0,
  top: -28,
  display: 'flex',
  alignItems: 'center',
} as const;

const ListRenderComponent = ({
  itemType,
  plugins,
  dispatch,
  items,
  sortable,
  listActions,
}: {
  itemType: Type;
  plugins: Plugins.PluginsType;
  dispatch: ExpressionEditorDispatch;
  items: Array<ContextualExpression>;
  sortable: boolean;
  listActions: Array<ListActionResult>;
}) => {
  const [itemIndex, setItemIndex] = React.useState<number>(-1);

  const ensureId = (
    item: ContextualExpression,
    index: number
  ): {
    item: ContextualExpression;
    id: string;
  } => ({
    item,
    id: String(index),
  });

  const openAddItemForm = () => {
    const item = getCurrentOrDefaultExpression(plugins)(itemType);
    setItemIndex(items.length);
    dispatch({
      key: emptyArray,
      type: 'EXPRESSION_LIST_ITEM_ADDED',
      payload: {item},
    });
  };

  const deleteListItem = (idx: PropertyListItemID) =>
    dispatch({
      key: emptyArray,
      type: 'EXPRESSION_LIST_ITEM_REMOVED',
      payload: {index: Number(idx)},
    });

  const shiftListItem = ({
    itemId,
    dropTargetId,
  }: {
    itemId: PropertyListItemID;
    dropTargetId: PropertyListItemID;
  }) =>
    dispatch({
      key: emptyArray,
      type: 'EXPRESSION_LIST_ITEM_MOVED',
      payload: {fromIndex: Number(itemId), toIndex: Number(dropTargetId)},
    });

  const renderNone = () => <PropertyListNone />;

  const renderListItem = (
    listItem: {
      item: ContextualExpression;
      id: string;
    },
    idx: number
  ) => {
    const listItemExpr = listItem.item;
    if (!listItemExpr) {
      // This shouldn't happen - we only render our popover if `listItemExpr`
      // is found and a valid expression. This `reportBug` is only here for a limited time
      // to confirm the bug https://webflow.atlassian.net/browse/PLAT-1792 is fixed.
      // This will be removed during October 2022 if no bugs reported.
      reportBug(
        'renderListItemNotFound',
        'renderListItem trying to open popover for a not found item',
        {
          itemType,
          items,
          listIdx: idx,
          team: 'platform',
          ticket: 'https://webflow.atlassian.net/browse/PLAT-1792',
        }
      );
      throw new TypeError(
        "List: Attempted to open a popover for an item whose value is 'undefined'"
      );
    }
    const renderPopoverContent = () => {
      return (
        <Wrapper>
          <ExpressionEditor
            dispatch={forwardDispatch(dispatch)({in: 'List', index: idx})}
            expression={listItemExpr}
          />
        </Wrapper>
      );
    };

    const props = (_openPopover: (arg1: unknown) => void) =>
      sortable
        ? {
            sortable,
            showIconsByDefault: true,
            editable: true,
            onEditButtonClicked: items.length ? _openPopover : noop,
            style: {height: '30px'},
          }
        : null;
    return (
      <DeprecatedPopoverBridge
        key={String(idx)}
        onChange={(state) => setItemIndex(state.open ? idx : -1)}
        state={idx === itemIndex ? OPENED_STATE : CLOSED_STATE}
        renderPopoverContent={renderPopoverContent}
        showArrow={false}
        showMask="TRANSPARENT"
        metricsIdentifier="expression-editor-settings-list-render-popover"
      >
        {({openPopover, Anchor}) => (
          <Anchor asChild={false}>
            {(ref: React.RefObject<any>) => (
              <PropertyListItem
                {...props(openPopover)}
                _ref={ref}
                key={idx}
                id={String(idx)}
                label={
                  <DataTypeSummary dataType={itemType} context={listItemExpr} />
                }
                onClick={items.length ? openPopover : undefined}
                onMouseOver={noop}
                onMouseOut={noop}
                onFocus={noop}
                onBlur={noop}
                icon={null}
              />
            )}
          </Anchor>
        )}
      </DeprecatedPopoverBridge>
    );
  };

  return (
    <>
      <div style={listHeaderActionsStyle}>
        <PropertyListAddButton
          tooltip="Add list item"
          data-automation-id="ListAddButton"
          onClick={openAddItemForm}
        />
        {listActions.map(({id, node}) => (
          <React.Fragment key={id}>{node}</React.Fragment>
        ))}
      </div>
      <View marginTop="$1">
        <PropertyList
          sortable={sortable}
          disabled={items.length === 0}
          deletable={items.length > 0}
          onItemDeleted={deleteListItem}
          onItemDropped={shiftListItem}
          items={items.map(ensureId)}
          renderNone={renderNone}
          renderItem={renderListItem}
        />
      </View>
    </>
  );
};

/*
 * Composite type reducers
 */

type UnionActionType = {
  type: 'TAG_CHANGED';
  payload: string;
};
// This is the inner parts of Expression Union format,
// we need to manually manage this here.
type UnionF = {
  tag: string;
  value: Expression;
};
const makeUnionReducer = singleMemo(
  (context: TypeImplementation.DataTypeEnvironmentContext) =>
    weakMemo((plugins: Plugins.PluginsType) =>
      weakMemo(
        (_dataType: Type) =>
          (action: UnionActionType): UnionF | null => {
            const shouldUseDefaultExpression =
              context === 'override' ||
              context === 'default' ||
              context === 'field-editor' ||
              context === 'compact';
            switch (action.type) {
              case 'TAG_CHANGED': {
                const tag = action.payload;
                const unionValues = Types.getUnionValues(_dataType) || {};
                const dataTypeAtTag = unionValues[tag];
                const recFields = Types.getRecordFields(
                  // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
                  Types.unwrapAliasType(dataTypeAtTag, plugins)
                );
                const isEmptyRecordType =
                  recFields && Object.keys(recFields).length === 0;
                const value = !shouldUseDefaultExpression
                  ? isEmptyRecordType
                    ? eERecord({})
                    : EHole()
                  : // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
                    getCurrentOrDefaultExpression(plugins)(dataTypeAtTag);

                return {
                  tag,
                  value,
                };
              }
              default: {
                /*:: (action: empty) */

                return null;
              }
            }
          }
      )
    )
);

function getLabelForType(dataType: Type, path: ExpressionPath): string {
  // this logic is copied from Types.getLabel
  const system = getSystem(dataType);
  if (system && typeof system.label === 'string') {
    return parsePropertyLabel(system.label).propertyName;
  }

  // but we want to fallback to another value if system label is not defined
  return getFallbackLabelFromExpressionPath(path);
}

function getFallbackLabelFromExpressionPath(path: ExpressionPath): string {
  const lastPathPart = path.length ? path[path.length - 1] : null;
  if (lastPathPart && lastPathPart.in === 'Record') {
    return lastPathPart.at;
  }
  return '';
}

/**
 * Given a binding expression, returns the TypeImplementation of the binding
 * type if the binding environment value has `preferTypeBindUI` set to true.
 *
 * This enables plugin values to define a custom binding UI that should be
 * used whenever the value is bound to a field.
 *
 * We
 */
function getPreferredTypeImplementationForBinding(
  expr: ContextualExpression,
  plugins: Plugins.PluginsType
): TypeImplementation.TypeImplementation | null {
  const {env, type} = getAnn(expr);

  if (!type) {
    return null;
  }

  const idAndPath = getBindingIdAndPath(env.context.values, stripAll(expr));

  if (!idAndPath || idAndPath.path.length > 0) {
    return null;
  }

  const envType = VariableMap.get(env.context.values, idAndPath.id);

  if (envType && envType.preferTypeBindUI) {
    const {ret} = Types.deconstructFunctionType(envType.dataType);
    return Types.getImplementation(ret, plugins);
  }

  return null;
}
